import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';
import { getCreatePageLinks, useGetCampaignName } from '@occc-app/routes/CampaignComponents/utils';
import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { useMakeOffersFilter } from '@occc-app/offerHelpers/hooks/use-make-offers-filter.hook';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';

import { messages } from '../../promocodes.messages';
import { routeCreator } from '../../promocodes.router';
import { PromocodesConfigurationFrom } from '../../components/PromocodesConfiguration/promocodes-configuration.form';
import { getConfigureFormOperations } from '../../components/PromocodesConfiguration/promocodes-configuration.operations';
import { transformOfferToInitialValues } from '../../components/PromocodesConfiguration/promocodes-configuration.utils';
import {
  PromocodesConfigureFormInitialValues,
  PromocodesConfigureFormValues,
} from '../../components/PromocodesConfiguration/promocodes-configuration.types';

export const PromocodesCreateController = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Promo Code Offer'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMakeOffersFilter();

  return (
    <OfferCreateConfigurePage<PromocodesConfigureFormValues, PromocodesConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Promocodes}
      engagementType={Offer_Engagement_Type.Promocode}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={transformOfferToInitialValues}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <PromocodesConfigurationFrom
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          currentFlowType={Flow_Types.Configure}
          makeOffersFilter={makeOffersFilter}
          isFormEditable
        />
      )}
    />
  );
};
